import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import type { AddressValues } from "@/lib/address-values"
import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type { Action } from "@/types/action"
import type { PermissionGroupKey } from "@/types/catalogue"
import type { Notice } from "@/types/notice"
import type { Standing } from "@/types/standing"

/** `App\Http\Resources\OrganisationPermissionResource`. */
export type OrganisationPermission = {
  id: string
  permission: string | null
  group: PermissionGroupKey | null
  name: string
  qualifiers: string[]
  active: boolean
  /** Given to every firm created from now on. */
  for_new_firms: boolean
  source: "manual" | "fca_register"
  /** Where it came from, when that is worth saying: "FCA Register". */
  source_label: string | null
  /** Held because an outside source shows it; it cannot be unticked. */
  locked: boolean
  locked_reason: string | null
  checked_at: string | null
  effective_on: string | null
}

/** `App\Http\Resources\FirmPermissionResource`. */
export type FirmPermission = {
  id: string
  organisation_permission_id: string
  permission: string | null
  group: PermissionGroupKey | null
  name: string
  qualifiers: string[]
  limitations: string[]
  principal_active: boolean
  created_at: string
  /** As the FCA Register writes it, and the category it files it under. */
  fca_name: string
  fca_category: string | null
  /** "Proposed 4 Nov 2026" or "Added 4 Nov 2026". */
  added_label: string
  actions: Action<"change_limits" | "remove">[]
}

/** The scope and what its pages show beside it (`meta`). */
export type FirmScope = {
  items: FirmPermission[]
  /** Proposed, in force, suspended or ended. */
  standing: Standing
  editable: boolean
  /** One per area of activity, in order, naming its permissions. */
  groups: { key: string; label: string; count: number; ids: string[] }[]
  /** What has lapsed, when anything has. */
  notice: Notice | null
  /** What removing a permission does, for the confirmation. */
  remove_detail: string
  appointment: {
    kind_label: string
    start_label: string
    /** What the appointment is waiting on; null when it is in force. */
    notice: Notice | null
    /** The appointment's notification to the FCA, while it is under way. */
    filing_id: string | null
  }
  /** The page's own buttons, in order, and what is in its menu. */
  actions: Action<"set_up" | "enter_frn" | "submit" | "change_scope">[]
  menu: Action<"change_scope" | "change_appointment">[]
}

type ApiFirmScope = {
  data: FirmPermission[]
  meta: Omit<FirmScope, "items">
}

function toFirmScope({ data, meta }: ApiFirmScope): FirmScope {
  return { items: data, ...meta }
}

export type FirmAddress = {
  id: string
  kind: "registered" | "trading"
  line1: string
  line2: string | null
  city: string | null
  county: string | null
  postcode: string | null
  country: string
  source: "manual" | "companies_house"
  /** "From Companies House · 4 Nov 2026". */
  provenance: string
  createdAt: string
  updatedAt: string
}

/** `App\Http\Resources\FirmAddressResource`. */
export type ApiFirmAddress = {
  id: string
  firm_id: string
  kind: FirmAddress["kind"]
  line1: string
  line2: string | null
  city: string | null
  county: string | null
  postcode: string | null
  country: string
  source: FirmAddress["source"]
  provenance: string
  created_at: string
  updated_at: string
}

export type FirmAddresses = {
  items: FirmAddress[]
  /** No trading address is held, so the firm trades from its registered one. */
  tradingSameAsRegistered: boolean
  /** Shown once: no trading address, or one that is the registered office. */
  tradesFromRegistered: boolean
}

/** The registered office Companies House holds today, as an address. */
export type RegisteredOffice = {
  companyNumber: string
  address: {
    line1: string
    line2: string
    city: string
    county: string
    postcode: string
    country: string
  } | null
}

type ApiCompanyProfile = {
  company_number: string
  registered_office_address: {
    premises: string | null
    line1: string | null
    line2: string | null
    city: string | null
    county: string | null
    postcode: string | null
    country: string | null
  } | null
}

export function toFirmAddress(data: ApiFirmAddress): FirmAddress {
  return {
    id: data.id,
    kind: data.kind,
    line1: data.line1,
    line2: data.line2,
    city: data.city,
    county: data.county,
    postcode: data.postcode,
    country: data.country,
    source: data.source,
    provenance: data.provenance,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

export const firmRecordQueries = {
  permissions: () =>
    queryOptions({
      queryKey: apiKey("/settings/permissions"),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<{ data: OrganisationPermission[] }>(
          "/settings/permissions",
          { signal }
        )

        return data
      },
    }),

  scope: (firmId: string) =>
    queryOptions({
      queryKey: apiKey(`/firms/${firmId}/permissions`),
      queryFn: async ({ signal }): Promise<FirmScope> =>
        toFirmScope(
          await apiFetch<ApiFirmScope>(`/firms/${firmId}/permissions`, {
            signal,
          })
        ),
    }),

  addresses: (firmId: string) =>
    queryOptions({
      queryKey: apiKey(`/firms/${firmId}/addresses`),
      queryFn: async ({ signal }): Promise<FirmAddresses> => {
        const response = await apiFetch<{
          data: ApiFirmAddress[]
          meta: {
            trading_same_as_registered: boolean
            trades_from_registered: boolean
          }
        }>(`/firms/${firmId}/addresses`, { signal })

        return {
          items: response.data.map(toFirmAddress),
          tradingSameAsRegistered: response.meta.trading_same_as_registered,
          tradesFromRegistered: response.meta.trades_from_registered,
        }
      },
    }),

  /**
   * Read live from Companies House, to offer before it is taken. A register
   * that cannot answer is nothing to offer rather than something to retry.
   */
  registeredOffice: (companyNumber: string) =>
    queryOptions({
      queryKey: apiKey(`/registers/companies-house/companies/${companyNumber}`),
      queryFn: async ({ signal }): Promise<RegisteredOffice> => {
        const { data } = await apiFetch<{ data: ApiCompanyProfile }>(
          `/registers/companies-house/companies/${companyNumber}`,
          { signal }
        )
        const office = data.registered_office_address
        const line1 = [office?.premises, office?.line1]
          .filter(Boolean)
          .join(" ")

        return {
          companyNumber: data.company_number,
          address:
            office && line1 !== ""
              ? {
                  line1,
                  line2: office.line2 ?? "",
                  city: office.city ?? "",
                  county: office.county ?? "",
                  postcode: office.postcode ?? "",
                  country: "GB",
                }
              : null,
        }
      },
      retry: false,
      staleTime: 5 * 60 * 1000,
    }),
}

/**
 * One address of either kind. A firm holds at most one of each, so it is
 * created the first time and amended after; the kind of a held address never
 * changes.
 */
export function useSaveFirmAddress(firmId: string) {
  return useMutation({
    mutationFn: async ({
      kind,
      held,
      values,
    }: {
      kind: FirmAddress["kind"]
      held: FirmAddress | undefined
      values: AddressValues
    }) => {
      const body = {
        line1: values.line1.trim(),
        line2: values.line2.trim() || null,
        city: values.city.trim() || null,
        county: values.county.trim() || null,
        postcode: values.postcode.trim() || null,
        country: values.country || "GB",
      }
      const { data } = await apiFetch<{ data: ApiFirmAddress }>(
        held
          ? `/firms/${firmId}/addresses/${held.id}`
          : `/firms/${firmId}/addresses`,
        {
          method: held ? "PATCH" : "POST",
          body: held ? body : { kind, ...body },
        }
      )

      return toFirmAddress(data)
    },
  })
}

/**
 * The registered address, as Companies House holds it. The API asks the
 * register again rather than taking an address from here, which is what lets
 * the result be recorded as the register's rather than as something typed.
 */
export function useTakeRegisteredOffice(firmId: string) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiFetch<{ data: ApiFirmAddress }>(
        `/firms/${firmId}/addresses/registered-office`,
        { method: "POST" }
      )

      return toFirmAddress(data)
    },
  })
}

export function useRemoveFirmAddress(firmId: string) {
  return useMutation({
    mutationFn: async (addressId: string) =>
      await apiFetch<void>(`/firms/${firmId}/addresses/${addressId}`, {
        method: "DELETE",
      }),
  })
}

export type ScopeEntry = {
  organisation_permission_id: string
  limitations: string[]
}

/** Sent whole: the scope is the answer to "what may this firm do". */
export function useSetScope(firmId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (permissions: ScopeEntry[]) =>
      toFirmScope(
        await apiFetch<ApiFirmScope>(`/firms/${firmId}/permissions`, {
          method: "PUT",
          body: { permissions },
        })
      ),
    onSuccess: (scope) => {
      queryClient.setQueryData(apiKey(`/firms/${firmId}/permissions`), scope)
    },
  })
}

/**
 * Sent whole, as the list of activities the principal holds. Firms holding a
 * permission that ends are reassessed in the background and broadcast.
 */
export function useSetOrganisationPermissions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (permissions: string[]) => {
      const { data } = await apiFetch<{ data: OrganisationPermission[] }>(
        "/settings/permissions",
        { method: "PUT", body: { permissions } }
      )

      return data
    },
    onSuccess: (held) => {
      queryClient.setQueryData(apiKey("/settings/permissions"), held)
    },
  })
}

export function useUpdateOrganisationPermission() {
  return useMutation({
    mutationFn: async ({
      id,
      forNewFirms,
    }: {
      id: string
      forNewFirms: boolean
    }) => {
      const { data } = await apiFetch<{ data: OrganisationPermission }>(
        `/settings/permissions/${id}`,
        { method: "PATCH", body: { for_new_firms: forNewFirms } }
      )

      return data
    },
  })
}
